import { tornTestUrl } from './tickets';

/**
 * The owner's projects (suggestion 152716-idea-2282863-86d1: "a section in the tickets area visible only to the owner
 * where we can list projects to be worked on. These crimes can be the first. We split them up by crime. We start at the
 * beginning and work our way through"). Server: apps/api/src/projects.ts; Owner → Tickets → Projects in Torn's Settings.
 */

export const PROJECT_STATUSES = ['todo', 'progress', 'done'] as const;
export type ProjectStatus = (typeof PROJECT_STATUSES)[number];
export const PROJECT_STATUS_NAMES: Record<ProjectStatus, string> = { todo: 'To do', progress: 'In progress', done: 'Done' };

export interface Project {
  id: string;
  title: string;
  status: ProjectStatus;
  /** His notes (what it is, where it stands, what's left). */
  notes: string;
  /** A Torn page it's about (https://www.torn.com/… only). */
  url?: string;
  createdAt: number;
  updatedAt: number;
}

/**
 * A project the developer adds for the owner with a release (docs/projects-new.json → apps/api/src/release-fixes.ts
 * RELEASE_PROJECTS; owner, 2026-10-29: "Feel free to add all this to projects too"). The server adds each `key` once, at
 * the end of his list as To do — if he later removes or edits it, it stays as he left it.
 */
export interface ReleaseProject {
  /** Stable, a-z 0-9 - (≤ 30): the project's id becomes `dev-<key>`. */
  key: string;
  title: string;
  notes?: string;
  url?: string;
}

/** One change from the owner: add (no id), edit, move up/down, or remove. */
export interface ProjectChange {
  id?: string;
  title?: string;
  status?: ProjectStatus;
  notes?: string;
  url?: string;
  move?: 'up' | 'down';
  remove?: boolean;
}

export const PROJECTS_MAX = 200;
export const PROJECT_TITLE_MAX = 120;
export const PROJECT_NOTES_MAX = 4_000;

const CRIMES = 'https://www.torn.com/page.php?sid=crimes#/';
const crime = (name: string, slug: string, status: ProjectStatus = 'todo', notes = ''): Omit<Project, 'id' | 'createdAt' | 'updatedAt'> => ({
  title: `Crime helper: ${name}`,
  status,
  notes: notes || `A helper for ${name} like the Bootlegging one: what to do next, the odds, the money — inside Torn's own crime page.`,
  url: `${CRIMES}${slug}`,
});

/** The first projects (only when there are none yet): one per Torn crime helper, in Torn's own crime order. */
export const SEED_PROJECTS: ReadonlyArray<Omit<Project, 'id' | 'createdAt' | 'updatedAt'>> = [
  crime('Search for Cash', 'searchforcash', 'progress'),
  crime('Bootlegging', 'bootlegging', 'done', 'Done — the model the others follow.'),
  crime('Graffiti', 'graffiti'),
  crime('Shoplifting', 'shoplifting'),
  crime('Pickpocketing', 'pickpocketing'),
  crime('Card Skimming', 'cardskimming'),
  crime('Burglary', 'burglary'),
  crime('Hustling', 'hustling'),
  crime('Disposal', 'disposal'),
  crime('Cracking', 'cracking'),
  crime('Forgery', 'forgery'),
  crime('Scamming', 'scamming'),
  crime('Arson', 'arson'),
];

const str = (x: unknown, max: number): string => (typeof x === 'string' ? x.trim().slice(0, max) : '');
const num = (x: unknown): number => (typeof x === 'number' && Number.isFinite(x) ? x : 0);

/** A project as kept: known fields, within limits; null without an id or a title. */
export function cleanProject(raw: unknown): Project | null {
  const o = raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : null;
  if (!o) return null;
  const id = typeof o.id === 'string' && /^[a-z0-9-]{1,40}$/.test(o.id) ? o.id : '';
  const title = str(o.title, PROJECT_TITLE_MAX).replace(/\s+/g, ' ');
  if (!id || !title) return null;
  const status = (PROJECT_STATUSES as readonly unknown[]).includes(o.status) ? (o.status as ProjectStatus) : 'todo';
  const url = tornTestUrl(o.url);
  return { id, title, status, notes: str(o.notes, PROJECT_NOTES_MAX), ...(url ? { url } : {}), createdAt: num(o.createdAt), updatedAt: num(o.updatedAt) };
}

/** A change as sent by the owner, checked (null: nothing usable). */
export function cleanProjectChange(raw: unknown): ProjectChange | null {
  const o = raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : null;
  if (!o) return null;
  const out: ProjectChange = {};
  if (typeof o.id === 'string' && /^[a-z0-9-]{1,40}$/.test(o.id)) out.id = o.id;
  if (typeof o.title === 'string') out.title = o.title.slice(0, PROJECT_TITLE_MAX);
  if ((PROJECT_STATUSES as readonly unknown[]).includes(o.status)) out.status = o.status as ProjectStatus;
  if (typeof o.notes === 'string') out.notes = o.notes.slice(0, PROJECT_NOTES_MAX);
  if (typeof o.url === 'string') out.url = o.url.slice(0, 500);
  if (o.move === 'up' || o.move === 'down') out.move = o.move;
  if (o.remove === true) out.remove = true;
  if (!out.id && !out.title?.trim()) return null;
  if (out.id && !out.move && !out.remove && out.title === undefined && out.status === undefined && out.notes === undefined && out.url === undefined) return null;
  return out;
}
